import { useState, useEffect, useCallback } from "react";
import { intent, type IntentLead } from "@/lib/api";
import Spinner from "@/components/Spinner";
import {
  CheckCircle, XCircle, ExternalLink, ChevronDown,
  Zap, Search, RefreshCw, Settings,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

// ── Score badge ───────────────────────────────────────────────────────────────
function ScoreBadge({ score }: { score: number }) {
  const color =
    score >= 8 ? "bg-green-100 text-green-700" :
    score >= 6 ? "bg-amber-100 text-amber-700" :
                 "bg-slate-100 text-slate-500";
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${color}`}>
      {score}/10
    </span>
  );
}

// ── Status pill ───────────────────────────────────────────────────────────────
function StatusPill({ status }: { status: IntentLead["status"] }) {
  const styles: Record<string, string> = {
    new:      "bg-blue-50 text-blue-700",
    approved: "bg-green-50 text-green-700",
    rejected: "bg-red-50 text-red-600",
    sent:     "bg-purple-50 text-purple-700",
  };
  return (
    <span className={`px-2 py-0.5 rounded-full text-[11px] font-bold capitalize ${styles[status] ?? ""}`}>
      {status}
    </span>
  );
}

// ── Lead card ─────────────────────────────────────────────────────────────────
function LeadCard({
  lead,
  onApprove,
  onReject,
  actioning,
}: {
  lead: IntentLead;
  onApprove: () => void;
  onReject: () => void;
  actioning: boolean;
}) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="bg-white rounded-2xl border border-black/[0.06] p-5 space-y-3">
      {/* Header row */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <ScoreBadge score={lead.intent_score} />
            <span className="text-xs font-semibold text-slate capitalize">{lead.source}</span>
            <StatusPill status={lead.status} />
            {lead.company && (
              <span className="text-xs px-2 py-0.5 rounded-full bg-black/[0.04] text-slate font-medium">
                {lead.company}
              </span>
            )}
          </div>
          <p className="mt-1.5 text-sm font-semibold text-ink line-clamp-2">
            {lead.post_text?.split("\n")[0] || "(no title)"}
          </p>
          <p className="text-xs text-slate mt-0.5">
            u/{lead.author_handle} ·{" "}
            {new Date(lead.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
          </p>
        </div>

        {/* Actions */}
        {lead.status === "new" && (
          <div className="flex items-center gap-1.5 shrink-0">
            <button
              onClick={onApprove}
              disabled={actioning}
              title="Approve — create subscriber & fire automation"
              className="p-2 rounded-xl bg-green-50 text-green-600 hover:bg-green-100 disabled:opacity-50 transition-colors"
            >
              <CheckCircle size={18} />
            </button>
            <button
              onClick={onReject}
              disabled={actioning}
              title="Reject"
              className="p-2 rounded-xl bg-red-50 text-red-500 hover:bg-red-100 disabled:opacity-50 transition-colors"
            >
              <XCircle size={18} />
            </button>
          </div>
        )}
      </div>

      {/* Post preview */}
      {lead.post_text && (
        <div>
          <p className={`text-xs text-slate leading-relaxed ${expanded ? "" : "line-clamp-3"}`}>
            {lead.post_text}
          </p>
          {lead.post_text.length > 200 && (
            <button
              onClick={() => setExpanded((e) => !e)}
              className="text-xs text-coral font-semibold mt-1 hover:underline"
            >
              {expanded ? "Show less" : "Show more"}
            </button>
          )}
        </div>
      )}

      {/* Footer */}
      {lead.source_url && (
        <a
          href={lead.source_url}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-xs text-slate hover:text-coral transition-colors"
        >
          <ExternalLink size={11} /> View on Reddit
        </a>
      )}
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
const STATUS_FILTERS = ["all", "new", "approved", "rejected"] as const;
type FilterStatus = (typeof STATUS_FILTERS)[number];

export default function LeadsPage() {
  const navigate = useNavigate();

  const [leads, setLeads] = useState<IntentLead[]>([]);
  const [nextToken, setNextToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [filter, setFilter] = useState<FilterStatus>("new");
  const [actioning, setActioning] = useState<string | null>(null);
  const [scanning, setScanning] = useState(false);
  const [scanMsg, setScanMsg] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (reset = true) => {
    if (reset) setLoading(true);
    setError(null);
    try {
      const res = await intent.listLeads({
        status: filter === "all" ? undefined : filter,
        maxResults: 20,
      });
      setLeads(res.leads);
      setNextToken(res.nextToken);
    } catch (e: any) {
      setError(e.message || "Failed to load leads");
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const loadMore = async () => {
    if (!nextToken) return;
    setLoadingMore(true);
    try {
      const res = await intent.listLeads({
        status: filter === "all" ? undefined : filter,
        maxResults: 20,
        nextToken,
      });
      setLeads((prev) => [...prev, ...res.leads]);
      setNextToken(res.nextToken);
    } catch (e: any) {
      setError(e.message || "Failed to load more");
    } finally {
      setLoadingMore(false);
    }
  };

  const handleApprove = async (id: string) => {
    setActioning(id);
    try {
      await intent.approveLead(id);
      load(); // re-fetch from server — no optimistic update to avoid stale state on failure
    } catch (e: any) {
      setError(e.message || "Failed to approve lead");
    } finally {
      setActioning(null);
    }
  };

  const handleReject = async (id: string) => {
    setActioning(id);
    try {
      await intent.rejectLead(id);
      load(); // re-fetch from server
    } catch (e: any) {
      setError(e.message || "Failed to reject lead");
    } finally {
      setActioning(null);
    }
  };

  const handleScanNow = async () => {
    setScanning(true);
    setScanMsg(null);
    try {
      const res = await intent.scanNow();
      setScanMsg(res.message);
      // Reload after short delay to pick up new leads
      setTimeout(() => load(), 5000);
    } catch (e: any) {
      setScanMsg(e.message || "Scan failed");
    } finally {
      setScanning(false);
    }
  };

  return (
    <div>
      {/* Header */}
      <div className="sticky top-0 z-10 bg-paper/90 backdrop-blur border-b border-black/[0.06] px-8 py-5 flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold flex items-center gap-2">
            <Zap size={18} className="text-coral" /> Lead Finder
          </h1>
          <p className="text-sm text-slate">Reddit posts with buying intent, detected automatically</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => navigate("/leads/settings")}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full border border-black/[0.08] text-sm font-semibold text-slate hover:border-black/20 transition-colors"
          >
            <Settings size={15} /> Keywords
          </button>
          <button
            onClick={handleScanNow}
            disabled={scanning}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-coral text-white text-sm font-semibold hover:bg-coral-dark disabled:opacity-60 transition-colors shadow-md shadow-coral/20"
          >
            <Search size={15} className={scanning ? "animate-pulse" : ""} />
            {scanning ? "Scanning…" : "Scan Now"}
          </button>
        </div>
      </div>

      <div className="p-8">
        {/* Scan message */}
        {scanMsg && (
          <div className="mb-4 p-3 rounded-xl bg-blue-50 border border-blue-200 text-sm text-blue-700">
            {scanMsg} — refreshing in 5 seconds…
          </div>
        )}

        {error && (
          <div className="mb-4 p-3 rounded-xl bg-red-50 border border-red-200 text-sm text-red-700">
            {error}
          </div>
        )}

        {/* Filter tabs */}
        <div className="flex gap-2 mb-6">
          {STATUS_FILTERS.map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-4 py-1.5 rounded-full text-xs font-semibold capitalize transition-colors ${
                filter === f
                  ? "bg-ink text-white"
                  : "bg-white text-slate border border-black/[0.06] hover:border-black/20"
              }`}
            >
              {f}
            </button>
          ))}
        </div>

        {/* Lead list */}
        {loading ? (
          <Spinner className="py-20" />
        ) : leads.length === 0 ? (
          <div className="py-20 text-center text-slate">
            <Zap size={32} className="mx-auto mb-3 opacity-30" />
            <p className="font-semibold">No leads found</p>
            <p className="text-sm mt-1">
              {filter === "new"
                ? "Add keywords and click Scan Now to start finding leads."
                : `No ${filter} leads yet.`}
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {leads.map((lead) => (
              <LeadCard
                key={lead.id}
                lead={lead}
                onApprove={() => handleApprove(lead.id)}
                onReject={() => handleReject(lead.id)}
                actioning={actioning === lead.id}
              />
            ))}
          </div>
        )}

        {/* Load more */}
        {nextToken && !loading && (
          <div className="flex justify-center mt-6">
            <button
              onClick={loadMore}
              disabled={loadingMore}
              className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full border border-black/[0.08] text-sm font-semibold text-slate hover:border-black/20 disabled:opacity-60 transition-colors"
            >
              <ChevronDown size={16} />
              {loadingMore ? "Loading…" : "Load more leads"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
